import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ImagePreviewStepProps {
  imageUrl: string;
  mode: 'avatar' | 'cover' | 'workbook-image';
  aspectRatio?: number;
  onTryAgain: () => void;
  onDone: () => void;
}

const ImagePreviewStep: React.FC<ImagePreviewStepProps> = ({
  imageUrl,
  mode,
  aspectRatio = 1,
  onTryAgain,
  onDone
}) => {
  const isAvatar = mode === 'avatar';
  const isCover = mode === 'cover';
  
  // Calculate preview dimensions based on mode
  const getPreviewStyle = () => {
    if (isAvatar) {
      return {
        width: '200px',
        height: '200px',
        borderRadius: '12px'
      };
    } else if (isCover) {
      return {
        width: '320px',
        height: '180px', // 16:9 aspect ratio
        borderRadius: '8px'
      };
    } else {
      // Workbook image
      const width = 280;
      const height = width / aspectRatio;
      return {
        width: `${width}px`,
        height: `${height}px`,
        borderRadius: '8px'
      };
    }
  };

  const previewStyle = getPreviewStyle();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center space-y-6"
    >
      {/* Success indicator */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="flex items-center space-x-2 text-green-600 dark:text-green-400"
      >
        <CheckCircle size={20} />
        <span className="font-medium">
          {isAvatar ? 'Profile photo generated!' : isCover ? 'Cover image generated!' : 'Image generated!'}
        </span>
      </motion.div>

      {/* Image preview */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        className="relative"
      >
        <div 
          className="relative overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700"
          style={previewStyle}
        >
          <img
            src={imageUrl}
            alt={isAvatar ? 'Generated profile photo' : isCover ? 'Generated cover image' : 'Generated image'}
            className="w-full h-full object-cover"
            style={{ borderRadius: previewStyle.borderRadius }}
          />
          
          {/* Overlay gradient for better text visibility if needed */}
          {isCover && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
          )}
        </div>
        
        {/* Avatar mode indicator */}
        {isAvatar && (
          <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
            <CheckCircle size={16} className="text-white" />
          </div>
        )}
      </motion.div>

      {/* Description */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center space-y-2"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isAvatar 
            ? 'Your profile photo has been generated and saved.' 
            : isCover 
            ? 'Your cover image has been generated and saved.'
            : 'Your image has been generated and saved.'}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500">
          You can generate a new one or continue with this image.
        </p>
      </motion.div>

      {/* Action buttons */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex space-x-3"
      >
        <Button
          variant="outline"
          onClick={onTryAgain}
          className="flex items-center space-x-2"
        >
          <RotateCcw size={16} />
          <span>Generate Another</span>
        </Button>
        
        <Button
          onClick={onDone}
          className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
        >
          <CheckCircle size={16} />
          <span>Perfect!</span>
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default ImagePreviewStep; 